<script>
   /*********************************************************************/
   /* Contributions widget                                              */
   /* --------------------                                              */
   /* plot variables contributions to q/h distances for selected object */
   /*********************************************************************/

	import { createEventDispatcher } from 'svelte';

   import { Axes, Multilines, Rectangles, Points, Lines, TextLabels, XAxis, YAxis, Box } from 'svelte-plots-basic/2d';
   import { scale as prep_scale } from 'mdatools/prep';
   import { Matrix, Vector, vector, tcrossprod } from 'mdatools/arrays';
   import { range } from 'mdatools/stat';

   import { pcamodel_get } from '../../../shared/wasm/dist/pca.js';

   import Container from '../../../shared/controls/Container.svelte';
   import Select from '../../../shared/controls/Select.svelte';

   export let selected;                   // ID of the selected sample
   export let model;                      // PCA model
   export let data;                       // dataset
   export let outliers = [];              // list of row indices excluded as outliers
   export let ncomp;


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   const dispatch = createEventDispatcher();

   // type of the contributions to show
   let type = 'residual';

   // x, y coordinates and variable label for highlighted bar
   let hx, hy, hl;

   /**
    * Mouse move handler
    * @param e mouse event
    */
   function mouseHandler(e) {

      if (e.target.tagName !== 'rect') {
         hx = -1;
         hy = null;
         hl = null;
         return;
      }

      const id = Array.from(e.target.parentNode.children).indexOf(e.target);
      if (id < 0) {
         hx = -1;
         hy = null;
         hl = null;
         return;
      }

      // if index is the same as the highlighted variable, do nothing
      if (hx === id) return;

      hx = id;
      hy = y.v[id];
      hl = model.varlabels[id];
   }


   // set x-axis values and limits
   let xValues, showSelection, limX;
   $: if (model) {
      showSelection = !(model.varvalues.length > 0);
      xValues = vector(showSelection ? model.varindices : model.varvalues);
      const n = xValues.length;
      const dil = (xValues.v[1] - xValues.v[0]) / 2;
      const dir = (xValues.v[n - 2] - xValues.v[n - 1]) / 2;
      const iRange = range(xValues);
      limX = [iRange[0] - dil, iRange[1] + dir];
   } else {
      showSelection = false;
      xValues = null;
      limX = [0, 1]
   }

   // compute coordinates of rectangles for selection if necessary
   $: left = showSelection ? xValues.apply(v => v - 0.5) : null;


   // compute main outcomes
   let xp, h, q, Xpt, Ht, Qt;
   $: if (selected >= 0 && model) {
      // get data values, vectors for centering and scaling
      const X = data.values;
      const m = new Vector(pcamodel_get(model, "mX"));
      const s = new Vector(pcamodel_get(model, "sX"));

      // autoscale the values
      const Xp = prep_scale(X, m, s);

      // get loadings for selected number of PCs and compute explained (H) and residual (Q) parts
      const P = new Matrix(pcamodel_get(model, "P", ncomp), model.ncols, ncomp);
      const PI = tcrossprod(P, P);
      const H = Xp.dot(PI);
      const Q = Xp.subtract(H);

      // transpose the outcomes to make subseting of objects faster
      Xpt = Xp.t();
      Ht = H.t();
      Qt = Q.t();

      // get outcomes for the selected object
      h = Ht.getcolumn(selected + 1);
      q = Qt.getcolumn(selected + 1);
      xp = Xpt.getcolumn(selected + 1);
   } else {
      h = null;
      q = null;
      xp = null;
   }

   // define y-axis values, limits and labels, depending on the selected type of outcomes
   let y, Y, limY, top, height, yLabel = '';
   $: if (type) {
         if (type === 'explained') {
            Y = Ht;
            y = h;
            yLabel = 'Score distance contributions (tpt)';
         } else if (type === 'residual'){
            Y = Qt;
            y = q;
            yLabel = 'Orthgonal distance contributions (e)';
         } else {
            Y = Xpt;
            y = xp;
            yLabel = 'Dataset (X)';
         }

         // y-value for the selected variable
         if (hx > -1 && showSelection) {
            hy = y.v[hx];
         }

         const yRange = range(Y.v);
         height = yRange[1] - yRange[0];
         limY = [yRange[0] - height/50, yRange[1] + height/15];

         top = Vector.fill(yRange[1], y.length)
      }

   // set plot title
   $: title = selected >= 0 ? `Object ${data.objlabels[selected]}` : null;
   $: xLabel = model.varvaluesName + (model.varvaluesUnits ? ', ' + model.varvaluesUnits : '');
</script>

{#if selected >= 0 && xValues && y}
<div class="drop">
   <div class="modal">
      <div class="header">
         <button class="close-button" on:click={() => dispatch("close")}>✕</button>
      </div>

      <div class="plot-container" role="tooltip" on:click={mouseHandler}>
         <Axes {title} {yLabel} {xLabel} {limY} {limX} margins={[0.75, 1.35, 0.5, 1.0]}>

            <!-- all outcomes -->
            <Multilines {xValues} yValues={Y} lineColor='#e0e0e0'/>

            <!-- outliers — show using diffeent color -->
            {#if outliers.length > 0}
            {#each outliers as ind}
            <Lines {xValues} yValues={Y.getcolumn(ind + 1)} lineColor="#ffdd88" />
            {/each}
            {/if}

            <!-- outcomes for the selected objects -->
            <Lines {xValues} yValues={y} lineColor="crimson"  />


            <!-- rectangles to make selection possible -->
            {#if showSelection}
            <Rectangles {left} {top} {height} width={1} faceColor='transparent' borderColor='transparent'/>

            {#if hx >= 0}
            <!-- highlighted variable -->
            <TextLabels xValues={[hx]} yValues={[top.v[0]]} labels={[hl]} pos={3} textSize={0.85}/>
            <Points xValues={[hx]} yValues={[hy]} faceColor='crimson' borderColor='crimson'/>
            <Rectangles left={[left.v[hx]]} top={[top.v[hx]]} {height} width={1} faceColor='#00000015'/>
            {/if}

            {/if}

            <Box slot="box" />
            <XAxis slot="xaxis" showGrid={true} />
            <YAxis slot="yaxis" showGrid={true} />
         </Axes>
      </div>

      {#if showSelection}
      <div class="msg-select">click inside the plot to see a variable label</div>
      {/if}
      <div class="plot-controls">
         <Container label=''>
            <Select id='type' bind:value={type} options={['full', 'explained', 'residual']} />
         </Container>
      </div>
   </div>

</div>
{/if}

<style>
   .drop {
      position: absolute;
      z-index: 9999;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #000000a0;
   }

   .modal {
      border-radius: 0.5rem;
      background-color: white;
      display: grid;
      grid-template-areas:
         "header"
         "plot"
         "message"
         "controls";
      grid-template-rows: 30px 1fr 20px 3em;
      width: 95%;
      height: 80%;
   }



   .plot-container {
      grid-area: plot;
      padding: 0.5em 1em;
   }

   .plot-controls {
      grid-area: controls;
      background: #f0f0f0;
      display: flex;
      width: 100%;
      justify-content: center;
      border-radius: 0.5rem;
   }

   .plot-controls > :global(.control-element) {
      background: transparent;
   }

   .header {
      grid-area: header;
   }

   .close-button {
      cursor: pointer;
      border: none;
      box-shadow: none;
      background: transparent;
      font-size: inherit;
      font-weight: bold;
      color: #f8ebc5;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      background-color: #9ea0a2;
      float: right;
      text-align: center;
      padding: 0;
      line-height: 20px;
      font-size: 12px;
      margin: 5px;
   }

   .close-button:hover {
      background-color: #4e5052;
   }


   .msg-select {
      grid-area: message;
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>